import { Card } from '@acceso/design-system';
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { GeneralLoading } from '@/components/GeneralStatus/GeneralLoading';
import { GeneralHeader } from '@/components/HeaderPage/HeaderPage';
import { SortDirectionDropdown } from '@/components/SortDirectionDropdown/SortDirectionDropdown';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useBroadcastTableResponsiveBreakpoint } from '@/hooks/useBroadcastTableResponsiveBreakpoint';
import { useUnderMinDesktopWidth } from '@/hooks/useUnderMinDesktopWidth';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';

import { TClip } from '../../types';

import { ClipBodyCardHeader } from './ClipBodyCardHeader';
import { ClipBodyCardList } from './ClipBodyCardList';

type TClipBodyCardProps = {
  clipsData: UseInfiniteQueryResult<InfiniteData<TClip[], unknown>, Error>;
};
export function ClipBodyCard({ clipsData }: TClipBodyCardProps) {
  const { clipItems, setVisibleBroadcastProgramCards } = useBroadcastScheduleProgram();
  const { underMinDesktopWith } = useUnderMinDesktopWidth();
  const { underBroadcastTableResponsiveBreakpoint } = useBroadcastTableResponsiveBreakpoint();
  const { visibleBroadcastProgramCards, pinnedId, toolkitComponents } =
    useBroadcastScheduleProgram();
  const { order_by, setFilters } = useProgramBroadcastFilters();
  const { t } = useTranslation();

  const allCardsVisible =
    visibleBroadcastProgramCards.segments &&
    visibleBroadcastProgramCards.segmentDetail &&
    pinnedId &&
    toolkitComponents[pinnedId];

  const sortDirection = order_by?.endsWith('asc') ? 'asc' : 'desc';

  const handleSortDirectionChange = (direction: string) => {
    setFilters({ order_by: `start_datetime ${direction}` });
  };

  return (
    <>
      {clipsData.isLoading ? (
        <GeneralLoading />
      ) : (
        <Card
          classes={{
            rootSection: clsx(
              '!min-w-full w-full flex-auto order-3 lg:order-1 overflow-auto max-h-[600px] lg:max-h-full',
              {
                'max-w-[22%]': allCardsVisible && underMinDesktopWith,
                'max-w-[25%]': allCardsVisible && !underMinDesktopWith,
              }
            ),
            container: 'py-size-4',
          }}
          id="clips-list"
        >
          <GeneralHeader
            name={t('Cortes')}
            onClose={() => {
              setVisibleBroadcastProgramCards(prev => {
                return {
                  ...prev,
                  segments: !prev.segments,
                };
              });
            }}
          />
          <div className="flex h-full flex-col gap-2">
            <div
              className={clsx(
                'flex justify-between gap-2 pl-2',
                underBroadcastTableResponsiveBreakpoint
                  ? 'flex-col items-start'
                  : 'flex-row items-center'
              )}
            >
              <ClipBodyCardHeader
                className={clsx(underBroadcastTableResponsiveBreakpoint && 'w-full')}
                items={clipItems || []}
              />
              <SortDirectionDropdown value={sortDirection} onChange={handleSortDirectionChange} />
            </div>
            <ClipBodyCardList items={clipItems || []} clipsData={clipsData} />
          </div>
        </Card>
      )}
    </>
  );
}
